// SPDX-FileCopyrightText: Copyright (c) 2026 NVIDIA CORPORATION & AFFILIATES. All rights reserved.
// SPDX-License-Identifier: Apache-2.0

import { GlobalStateService } from '../services/global.service.js';
import { LogService } from '../services/log.service.js';
import type { ElementDefinition } from '../types/index.js';
import { isFocusable } from './focus.js';

export function getChildren(node: Document | Node | HTMLElement): Element[] {
  if (node instanceof Document && node.documentElement) {
    return Array.from(node.documentElement.children); // root document children
  } else if (node instanceof HTMLElement && node.shadowRoot) {
    return Array.from(node.shadowRoot.children); // shadow root direct children
  } else if (node instanceof HTMLSlotElement && node.assignedElements) {
    const slotted = node.assignedElements(); // slotted elements
    return slotted.length ? slotted : Array.from(node.children); // slot fallback
  } else {
    return Array.from((node as HTMLElement).children); // light DOM direct children
  }
}

export function generateId() {
  const uint32 = self.crypto.getRandomValues(new Uint32Array(1))[0]!;
  return `_${uint32.toString(16)}`;
}

export function getAttributeChanges(element: HTMLElement, attr: string, fn: (attrValue: string | null) => void) {
  fn(element.getAttribute(attr));
  const observer = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      if (mutation.attributeName === attr) {
        fn((mutation.target as HTMLElement).getAttribute(attr));
      }
    });
  });
  observer.observe(element, { attributes: true, subtree: true });
  return observer;
}

export function getAttributeListChanges(element: HTMLElement, attrs: string[], fn: (mutation: MutationRecord) => void) {
  const observer = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      if (mutation.attributeName && attrs.includes(mutation.attributeName)) {
        fn(mutation);
      }
    });
  });
  observer.observe(element, { attributes: true, subtree: true });
  return observer;
}

/**
 * Appends styles to the render root of a given element. This is useful for
 * styles that apply outside the scope of the element's shadow
 * root such as UA styles --webkit-* that won't work in a ::slotted selector
 */
export function appendRootNodeStyle(host: HTMLElement, styles: string) {
  const stylesheet = new CSSStyleSheet();
  stylesheet.replaceSync(styles);
  const root = host.getRootNode() as Document | ShadowRoot;
  if (root.adoptedStyleSheets) {
    const hasStyleSheet = root.adoptedStyleSheets
      .map(s => styleSheetToString(s))
      .find(s => s === styleSheetToString(stylesheet));
    if (!hasStyleSheet) {
      root.adoptedStyleSheets = [...Array.from(root.adoptedStyleSheets), stylesheet];
    }
  }
  return stylesheet;
}

export function styleSheetToString(stylesheet: CSSStyleSheet) {
  return stylesheet.cssRules
    ? Array.from(stylesheet.cssRules)
        .map(rule => rule.cssText || '')
        .join('\n')
    : '';
}

/** Intercepts a property setter on an element so `callback` fires on every assignment. Does not use a MutationObserver. */
export function getPropertyChanges(element: HTMLElement, key: string, callback: (value: unknown) => void) {
  const proto = Object.getPrototypeOf(element);
  const descriptor = Object.getOwnPropertyDescriptor(proto, key);
  if (!descriptor) return;
  const own = Object.getOwnPropertyDescriptor(element, key);
  if (own && own.configurable === false) return;
  Object.defineProperty(element, key, {
    configurable: true,
    get: descriptor.get,
    set: (val: unknown) => {
      descriptor.set!.call(element, val);
      callback(val);
    }
  });

  return () => {
    if (own) {
      Object.defineProperty(element, key, own);
      return;
    }

    Reflect.deleteProperty(element, key);
  };
}

/* used for cases of needing to know a property update outside of lit, example a native input value prop change */
export function getElementUpdate(element: HTMLElement, key: string, callback: (value: unknown) => void) {
  if (element.hasAttribute(key)) {
    callback(element.getAttribute(key));
  } else if ((element as unknown as Record<string, unknown>)[key] !== undefined) {
    callback((element as unknown as Record<string, unknown>)[key]);
  }

  const cleanupPropertyChanges = getPropertyChanges(element, key, callback);
  const observer = getAttributeChanges(element, key, val => callback(val));
  const disconnect = observer.disconnect.bind(observer);

  observer.disconnect = () => {
    cleanupPropertyChanges?.();
    disconnect();
  };

  return observer;
}

export function clickOutsideElementBounds(event: PointerEvent | MouseEvent, element: HTMLElement) {
  const { left, right, top, bottom } = element.getBoundingClientRect();
  return event.clientX < left || event.clientX > right || event.clientY < top || event.clientY > bottom;
}

/** parses number out of basic calc generated by the design tokens */
export function parseTokenNumber(value: string) {
  return parseInt(value.includes('calc') ? value.split('*')[1]!.replace('px)', '').trim() : value, 10) || 0;
}

export function getCustomElementRegistry(element: Element): CustomElementRegistry | undefined {
  const root = element.getRootNode();
  const CustomElementRegistryConstructor =
    element.ownerDocument.defaultView?.CustomElementRegistry ?? globalThis.CustomElementRegistry;
  if (
    CustomElementRegistryConstructor &&
    'customElementRegistry' in root &&
    root.customElementRegistry instanceof CustomElementRegistryConstructor
  ) {
    return root.customElementRegistry;
  }
  const documentRegistry = element.ownerDocument.defaultView?.customElements;
  if (documentRegistry) return documentRegistry;
  return typeof customElements === 'undefined' ? undefined : customElements;
}

/** true if the browser supports scoped custom element registries */
export const supportsScopedRegistry =
  globalThis.CustomElementRegistry && 'initialize' in CustomElementRegistry.prototype;

/** defines an element with the given custom element registry */
export function defineElement(element: ElementDefinition, customElementRegistry: CustomElementRegistry) {
  Object.entries(element.elementDefinitions ?? {})
    .filter(([_tag, e]) => !customElementRegistry.get(e.metadata.tag))
    .forEach(([_tag, e]) => {
      defineElement(e, customElementRegistry);
    });

  if (!customElementRegistry.get(element.metadata.tag)) {
    customElementRegistry.define(element.metadata.tag, element);

    if (customElementRegistry === customElements) {
      GlobalStateService.dispatch('NVE_ELEMENT_DEFINE', {
        elementRegistry: { [element.metadata.tag]: element.metadata.version }
      });
    }
  }
}

/** defines an element with the global custom element registry */
export function define(element: ElementDefinition) {
  const { version } = element.metadata;

  // if scoped registry supported, register only the root element to global registry
  if (supportsScopedRegistry && !customElements.get(element.metadata.tag)) {
    customElements.define(element.metadata.tag, element);
    GlobalStateService.dispatch('NVE_ELEMENT_DEFINE', {
      elementRegistry: { [element.metadata.tag]: element.metadata.version }
    });
  } else {
    // if scoped registry not supported, register the element and all its children to the global registry
    defineElement(element, customElements);
  }

  // warn on duplicate version
  if (
    GlobalStateService.state.elementRegistry[element.metadata.tag] !== version &&
    globalThis?.location?.hostname === 'localhost'
  ) {
    void import('./audit-logs.js').then(m =>
      console.warn(m.getDuplicatePackageVersionWarning(element.metadata.tag, version))
    );
  }
}

export function isContextMenuClick(event: MouseEvent) {
  return (event.buttons === 2 && !event.ctrlKey) || (event.buttons === 1 && event.ctrlKey);
}

export function getFlattenedFocusableItems(element: Node, depth = 10) {
  return getFlattenedDOMTree(element, depth).filter((e: HTMLElement) => isFocusable(e)) as HTMLElement[];
}

/**
 * Preserves visual DOM ordering when using slots within Shadow DOM
 * See more information/resources on Shadow DOM linear traversal
 * https://nolanlawson.com/2021/02/13/managing-focus-in-the-shadow-dom/
 * https://www.abeautifulsite.net/posts/querying-through-shadow-roots/
 */
export function getFlattenedDOMTree(node: Node, depth = 10): HTMLElement[] {
  return (
    Array.from(getChildren(node)).reduce(
      (p: unknown[], n: Node) => [
        ...p,
        [n, [...Array.from(getChildren(n)).map(i => [i, getFlattenedDOMTree(i, depth)])]]
      ],
      []
    ) as unknown as HTMLElement[]
  ).flat(depth);
}

export function validKeyNavigationCode(e: KeyboardEvent): e is KeyboardEvent & { code: KeynavCode } {
  return keynavCodes.has(e.code);
}

export enum KeynavCode {
  End = 'End',
  Home = 'Home',
  PageUp = 'PageUp',
  PageDown = 'PageDown',
  ArrowUp = 'ArrowUp',
  ArrowDown = 'ArrowDown',
  ArrowLeft = 'ArrowLeft',
  ArrowRight = 'ArrowRight'
}

const keynavCodes: Set<string> = new Set(Object.values(KeynavCode));

export function hasInvalidDOMGrid(rows: HTMLElement[]) {
  return rows.some(row => row.children.length !== rows[0]!.children.length);
}

export function removeEmptyTextNode(node: Node) {
  if ((node as Text)?.wholeText?.trim() === '') {
    (node as Text).remove();
  }
}

export function scrollBarWidth() {
  const div = globalThis.document.createElement('div');
  div.style.setProperty('width', '100px');
  div.style.setProperty('height', '100px');
  div.style.setProperty('overflow', 'scroll');
  div.style.setProperty('position', 'absolute');
  div.style.setProperty('top', '-9999px');
  globalThis.document.body.appendChild(div);
  const width = div.offsetWidth - div.clientWidth;
  globalThis.document.body.removeChild(div);
  return width;
}

export function hasScrollBar(el: HTMLElement) {
  return el.scrollHeight > el.clientHeight;
}

export function hasHorizontalScrollBar(el: HTMLElement) {
  return el.scrollWidth > el.clientWidth;
}

export function endOfScrollBox(element: HTMLElement, offset = 0) {
  return element.scrollTop + element.offsetHeight + offset >= element.scrollHeight;
}

export async function openEyeDropper(): Promise<string> {
  const EyeDropperConstructor = (
    globalThis as unknown as { EyeDropper?: new () => { open: () => Promise<{ sRGBHex: string }> } }
  ).EyeDropper;

  if (!EyeDropperConstructor) {
    LogService.warn('EyeDropper API is unavailable');
    return '';
  }

  try {
    return (await new EyeDropperConstructor().open()).sRGBHex;
  } catch (e) {
    LogService.warn(`EyeDropper selection failed: ${(e as Error).message}`);
    return '';
  }
}

/**
 * provides a object with key/value of the currently applied design tokens
 */
export function getThemeTokens(element = globalThis.document.querySelector(':root')!) {
  // default root or provided element
  const styles = getComputedStyle(element);
  let parent: CSSStyleSheet[] = [];

  // check for parent iframe, same domain iframes change how the browser accesses style sheets
  try {
    parent = Array.from(globalThis.parent.document.styleSheets);
  } catch {
    // if not accessible, skip
  }

  return [...parent, ...Array.from(globalThis.document.styleSheets)]
    .reduce((finalArr: string[][], sheet) => {
      let rules: CSSRule[] = [];
      try {
        rules = [...sheet.cssRules];
      } catch {
        // if the style sheet is not accessible, skip
      }
      return finalArr.concat(
        rules
          .filter(rule => rule.type === 1)
          .reduce((propValArr: string[][], rule) => {
            const props = [...(rule as CSSStyleRule).style]
              .filter((p: string) => p.trim().includes('--nve'))
              .map((propName: string) => [
                propName.trim(),
                (rule as CSSStyleRule).style.getPropertyValue(propName).trim()
              ]);
            return [...propValArr, ...props];
          }, [])
      );
    }, [])
    .reduce(
      (p: Record<string, string>, token: string[]) => ({ ...p, [token[0]!]: styles.getPropertyValue(token[0]!) }),
      {}
    );
}

export function removeEmptySlotWhitespace(slot: HTMLSlotElement) {
  if (slotContainsOnlyWhitespace(slot)) {
    slot.assignedNodes().forEach((node: Node) => node.parentNode?.removeChild(node));
  }
}

export function slotContainsOnlyWhitespace(slot: HTMLSlotElement) {
  const hasNoElements = !slot.assignedNodes().find(i => i.nodeType !== Node.TEXT_NODE);
  const hasOnlyEmptyTextNodes = !slot
    .assignedNodes()
    .find(i => i.nodeType === Node.TEXT_NODE && (i.textContent ?? '').trim() !== '');
  return hasNoElements && hasOnlyEmptyTextNodes;
}

export function applySlotContentStates(slot: HTMLSlotElement, element: HTMLElement & { _internals: ElementInternals }) {
  const nodes = slot.assignedNodes();
  const name = slot.name ?? 'slot';

  if (nodes.length) {
    element._internals.states.add(`has-${name}`);
  } else {
    element._internals.states.delete(`has-${name}`);
  }
}

export function getDisplayValue(option?: { label?: string; value?: string }): string {
  return option?.label || option?.value || '';
}

/** determines if HTML element matches the given component tag */
export function matchesElementName(element: Partial<HTMLElement>, component: { metadata: { tag: string } }) {
  return element.localName === component.metadata.tag;
}

export function createGhostElement(sourceElement: HTMLElement) {
  const ghost = globalThis.document.createElement('div');
  const { width } = sourceElement.getBoundingClientRect();
  ghost.setAttribute('nve-ghost', '');
  ghost.style.minWidth = `${width}px`;
  ghost.style.maxWidth = `${width}px`;
  ghost.style.height = '1px';
  ghost.slot = sourceElement.slot;
  return ghost;
}

export function sameRenderRoot(element1: HTMLElement, element2: HTMLElement) {
  const root1 = element1.getRootNode();
  const root2 = element2.getRootNode();
  const sameShadowRoot = root1 instanceof ShadowRoot && root2 instanceof ShadowRoot && root1 === root2;
  const sameDocumentRoot = root1 === globalThis.document && root2 === globalThis.document;
  const sameUnattachedRoot = root1 === root2;
  return sameShadowRoot || sameDocumentRoot || sameUnattachedRoot;
}

export function getAnchorNames(element: HTMLElement): string[] {
  return (
    element.style.anchorName
      ?.split(',')
      ?.map(name => name.trim())
      ?.filter(name => name !== 'none' && name !== '') ?? []
  );
}

export function removeAnchorName(element: HTMLElement, name: string) {
  const anchorNames = getAnchorNames(element);
  element.style.anchorName = anchorNames.filter(i => i !== name).join(',');
}

export function appendAnchorName(element: HTMLElement, name: string) {
  element.style.anchorName = Array.from(new Set([...getAnchorNames(element), name])).join(',');
}

/**
 * Returns true when both arrays have the same length and each index is strictly equal (`===`).
 */
export function sameOrderedStringArray(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

/**
 * Returns unique non-empty strings in first-seen order (insertion order of a `Set` over the filtered list).
 */
export function uniqueNonEmptyStrings(values: Iterable<string | null | undefined>): string[] {
  return Array.from(
    new Set(Array.from(values).filter((value): value is string => typeof value === 'string' && value.length > 0))
  );
}
